import {
    type IncomeSplitRule,
    IncomeSplitRuleType,
    PaycheckRoutingRuleAccountType,
} from '@fruitful/income-split';
import { Redirect, router } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    isEmpty,
    Notice,
    redactPrivate,
    ScreenContainer,
    XStack,
    YStack,
} from 'smoothie';

import { uuid } from '../../../common/misc';
import { useInternalInvestmentAccountsForIncomeSplit } from '../../hooks/income-split/useInternalInvestmentAccountsForIncomeSplit';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import {
    incomeSplitAtom,
    incomeSplitRuleEditorModeAtom,
    selectedIncomeRuleAtom,
} from '../../state/PaycheckRoutingAtoms';
import RuleEditorButtons from '../paycheck-routing/income-account-routing/rule-editor/common/RuleEditorButtons';
import RuleEditorAccount from './editor/RuleEditorAccountNew';
import RuleEditorAmount from './editor/RuleEditorAmount';
import RuleEditorDeleteSheet from './editor/RuleEditorDeleteSheet';
import RuleEditorDescription from './editor/RuleEditorDescription';
import RuleEditorPercentage from './editor/RuleEditorPercentageNew';
import IncomeSplitContributionSheet from './IncomeSplitContributionLimitInfoSheet';
import IncomeSplitTargetBalanceInfoSheet from './IncomeSplitTargetBalanceInfoSheet';

const IncomeSplitRuleEditorNew = () => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);
    const { bankAccountDetails } = useGetAllBankAccountDetailsFromGlobalState();
    const [selectedRule, setSelectedRule] = useAtom(selectedIncomeRuleAtom);
    const incomeSplitRuleEditorMode = useAtomValue(
        incomeSplitRuleEditorModeAtom,
    );

    const [isRuleValid, setIsRuleValid] = useState(false);
    const [isTargetBalanceInfoSheetOpen, setIsTargetBalanceInfoSheetOpen] =
        useState(false);
    const [
        isContributionLimitInfoSheetOpen,
        setIsContributionLimitInfoSheetOpen,
    ] = useState(false);
    const [contributionMaxType, setContributionMaxType] = useState<
        'Monthly' | 'Annual' | null
    >(null);
    const [maxContributionRowWidth, setMaxContributionRowWidth] = useState(0);
    const [showDeleteSheet, setShowDeleteSheet] = useState(false);

    const { data: depositAccounts } = useGetInternalAndExternalDepositAccounts(
        {},
    );
    const {
        internalInvestmentAccounts,
        isLoading: isLoadingInternalInvestmentAccounts,
    } = useInternalInvestmentAccountsForIncomeSplit();

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const {
        id,
        targetBalanceCents,
        accountId,
        type,
        percentage,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
        description,
        incomeSplitRuleSetId,
    } = selectedRule ?? {};

    const selectedRuleAccount = externalAccounts.find(
        (a) => a.id === selectedRule?.accountId,
    );
    const selectedRuleApexAccount = (internalInvestmentAccounts ?? []).find(
        (a) => a.apexAccountId === selectedRule?.accountId,
    );
    const ruleAccountIsExternal =
        selectedRuleAccount?.isExternal ?? selectedRule?.isExternal ?? false;
    const ruleAccountIsApex =
        selectedRuleApexAccount != null ||
        selectedRule?.accountType ===
            PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT;

    const ruleSet = incomeSplit?.ruleSets.find(
        (r) => r.id === incomeSplitRuleSetId,
    );

    const totalUsed = (ruleSet?.rules ?? [])
        .filter((r) => r.id !== selectedRule?.id)
        .reduce((sum, r) => sum + (r.percentage ?? 0), 0);

    const current = selectedRule?.percentage ?? null;

    const percentageRemaining =
        current == null || current === 0
            ? Math.max(0, 100 - totalUsed)
            : Math.max(0, 100 - (totalUsed + current));

    useEffect(() => {
        let isValid = false;

        switch (selectedRule?.type) {
            case IncomeSplitRuleType.MINIMUM_BALANCE:
                isValid = percentage !== 0 && accountId != null && type != null;
                break;
            case IncomeSplitRuleType.PERCENTAGE:
                isValid = percentage !== 0 && accountId != null && type != null;
                break;
        }

        setIsRuleValid(isValid);
    }, [selectedRule]);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    // if the member is adding a new rule, lets prompt them to select the destination account first
    useEffect(() => {
        if (incomeSplitRuleEditorMode === 'Add') {
            router.navigate('/income-split/edit/account?tiedRules=true');
        }
    }, []);

    if (
        !shouldSeeIncomeSplit ||
        !incomeSplit ||
        bankAccountDetails.length < 1 ||
        !selectedRule ||
        !incomeSplitRuleEditorMode
    ) {
        return <Redirect href="/" />;
    }

    if (isLoadingInternalInvestmentAccounts) {
        return <FullScreenSpinner />;
    }

    const finishEditing = () => {
        router.back();
        setSelectedRule(null);
    };

    return (
        <>
            <StatusBar style="light" />
            <ScreenContainer
                title={`${incomeSplitRuleEditorMode} rule`}
                hasBackButton={true}
                onBack={() => {
                    finishEditing();
                }}
                useSafeAreaInsetTop={false}
                rightAction={
                    incomeSplitRuleEditorMode === 'Edit' ? (
                        <XStack
                            height="100%"
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            <Button
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.SMALL}
                                width="auto"
                                tone="danger"
                                text="Delete"
                                onPress={() => {
                                    setShowDeleteSheet(true);
                                }}
                            />
                        </XStack>
                    ) : undefined
                }
            >
                <YStack height="100%" gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <YStack gap={16} {...redactPrivate()}>
                            <RuleEditorDescription
                                description={description}
                                updateDescription={(description) => {
                                    setSelectedRule({
                                        ...selectedRule,
                                        description,
                                    });
                                }}
                            />
                            <RuleEditorAccount
                                bankAccounts={bankAccountDetails}
                                externalAccounts={externalAccounts}
                                internalInvestmentAccounts={
                                    internalInvestmentAccounts ?? []
                                }
                                rule={selectedRule ?? {}}
                                cardProps={{ paddingVertical: 11 }}
                            />
                            {selectedRuleApexAccount?.backdoorRothRole ===
                                'ROTH' && (
                                <Notice
                                    variant="warning"
                                    title="Backdoor Roth IRA account"
                                    description="This account is part of a backdoor Roth conversion strategy. Direct contributions may conflict with this strategy. Consult your Guide for questions."
                                />
                            )}
                            <RuleEditorPercentage
                                amount={
                                    percentage != null && percentage > 0
                                        ? percentage
                                        : undefined
                                }
                                updateAmount={(amount) => {
                                    setSelectedRule({
                                        ...selectedRule,
                                        percentage: amount,
                                    });
                                }}
                                percentageRemaining={percentageRemaining}
                            />
                            <RuleEditorAmount
                                amount={
                                    ruleAccountIsExternal || ruleAccountIsApex
                                        ? 0
                                        : (targetBalanceCents ?? 0)
                                }
                                disabled={
                                    ruleAccountIsExternal || ruleAccountIsApex
                                }
                                updateAmount={(amount) => {
                                    setSelectedRule({
                                        ...selectedRule,
                                        targetBalanceCents:
                                            amount > 0 ? amount : 0,
                                        type:
                                            amount > 0
                                                ? IncomeSplitRuleType.MINIMUM_BALANCE
                                                : IncomeSplitRuleType.PERCENTAGE,
                                    });
                                }}
                                label="Target balance (optional)"
                                openInfoSheet={() => {
                                    setIsTargetBalanceInfoSheetOpen(true);
                                }}
                            />
                            <XStack
                                width="100%"
                                gap={16}
                                onLayout={(e) => {
                                    setMaxContributionRowWidth(
                                        e.nativeEvent.layout.width,
                                    );
                                }}
                            >
                                <RuleEditorAmount
                                    amount={monthlyContributionMaxCents}
                                    updateAmount={(amount) => {
                                        setSelectedRule({
                                            ...selectedRule,
                                            monthlyContributionMaxCents: amount,
                                        });
                                    }}
                                    label="Monthly limit (optional)"
                                    openInfoSheet={() => {
                                        setContributionMaxType('Monthly');
                                        setIsContributionLimitInfoSheetOpen(
                                            true,
                                        );
                                    }}
                                    width={maxContributionRowWidth / 2 - 8}
                                />
                                <RuleEditorAmount
                                    amount={yearlyContributionMaxCents}
                                    updateAmount={(amount) => {
                                        setSelectedRule({
                                            ...selectedRule,
                                            yearlyContributionMaxCents: amount,
                                        });
                                    }}
                                    label="Annual limit (optional)"
                                    openInfoSheet={() => {
                                        setContributionMaxType('Annual');
                                        setIsContributionLimitInfoSheetOpen(
                                            true,
                                        );
                                    }}
                                    width={maxContributionRowWidth / 2 - 8}
                                />
                            </XStack>
                        </YStack>
                    </YStack>

                    <RuleEditorButtons
                        onSave={() => {
                            if (incomeSplitRuleEditorMode === 'Edit') {
                                setIncomeSplit({
                                    ...incomeSplit,
                                    ruleSets: incomeSplit.ruleSets.map(
                                        (ruleSet) =>
                                            ruleSet.id === incomeSplitRuleSetId
                                                ? {
                                                      ...ruleSet,
                                                      rules: ruleSet.rules.map(
                                                          (rule) =>
                                                              rule.id ===
                                                              selectedRule.id
                                                                  ? ({
                                                                        ...selectedRule,
                                                                        id:
                                                                            selectedRule.id ??
                                                                            uuid(),
                                                                        incomeSplitConfigId:
                                                                            selectedRule.incomeSplitConfigId ??
                                                                            incomeSplit.id,
                                                                        incomeSplitRuleSetId:
                                                                            selectedRule.incomeSplitRuleSetId ??
                                                                            incomeSplitRuleSetId,
                                                                    } as unknown as IncomeSplitRule)
                                                                  : rule,
                                                      ),
                                                  }
                                                : ruleSet,
                                    ),
                                });
                            } else {
                                setIncomeSplit({
                                    ...incomeSplit,
                                    ruleSets: incomeSplit.ruleSets.map(
                                        (ruleSet) =>
                                            ruleSet.id === incomeSplitRuleSetId
                                                ? {
                                                      ...ruleSet,
                                                      rules: [
                                                          ...(ruleSet.rules ??
                                                              []),
                                                          {
                                                              ...selectedRule,
                                                              id:
                                                                  selectedRule.id ??
                                                                  uuid(),
                                                          } as unknown as IncomeSplitRule,
                                                      ],
                                                  }
                                                : ruleSet,
                                    ),
                                });
                            }
                            finishEditing();
                        }}
                        onCancel={finishEditing}
                        disableSave={!isRuleValid}
                    />
                </YStack>
            </ScreenContainer>
            <IncomeSplitContributionSheet
                isOpen={isContributionLimitInfoSheetOpen}
                onClose={() => {
                    setIsContributionLimitInfoSheetOpen(false);
                    setContributionMaxType(null);
                }}
                contributionMaxType={contributionMaxType}
            />
            <IncomeSplitTargetBalanceInfoSheet
                isOpen={isTargetBalanceInfoSheetOpen}
                onClose={() => {
                    setIsTargetBalanceInfoSheetOpen(false);
                }}
            />
            <RuleEditorDeleteSheet
                isOpen={showDeleteSheet}
                onClose={() => {
                    setShowDeleteSheet(false);
                }}
                onDelete={() => {
                    if (isEmpty(id) || isEmpty(incomeSplitRuleSetId)) return;

                    setIncomeSplit({
                        ...incomeSplit,
                        ruleSets: incomeSplit.ruleSets.map((group) =>
                            group.id === incomeSplitRuleSetId
                                ? {
                                      ...group,
                                      rules: group.rules.filter(
                                          (rule) => rule.id !== id,
                                      ),
                                  }
                                : group,
                        ),
                    });

                    setShowDeleteSheet(false);
                    router.back();
                }}
            />
        </>
    );
};

export default IncomeSplitRuleEditorNew;
